import type { Rpc } from "@effect/rpc";
import type { FunctionReference, RegisteredQuery } from "convex/server";
import { Atom, type Result } from "@effect-atom/atom";
import { Data, Effect, Exit, type Layer, Stream } from "effect";

import { ConvexClient } from "../client";
import type { AnyRpcModule, RpcResultEncoded, RpcEndpoint } from "./server";

export class RpcDefectError extends Data.TaggedError("RpcDefectError")<{
	readonly defect: unknown;
}> {
	get message(): string {
		return `RpcDefectError: ${extractDefectMessage(this.defect)}`;
	}
}

/** Best-effort extraction of a human-readable message from an opaque defect. */
const extractDefectMessage = (defect: unknown): string => {
	if (defect === null || defect === undefined) return "Unknown defect";
	if (typeof defect === "string") return defect;
	if (defect instanceof Error) return defect.message;
	if (typeof defect === "object") {
		const obj = defect as Record<string, unknown>;
		// TaggedError / Data.TaggedError
		if (typeof obj._tag === "string" && typeof obj.message === "string") {
			return `[${obj._tag}] ${obj.message}`;
		}
		if (typeof obj._tag === "string") return obj._tag;
		if (typeof obj.message === "string") return obj.message;
		try {
			return JSON.stringify(defect);
		} catch {
			return String(defect);
		}
	}
	return String(defect);
};

type EndpointPayload<E> = E extends RpcEndpoint<infer _Tag, infer R, infer _ConvexFn>
	? Rpc.Payload<R>
	: never;

type EndpointSuccess<E> = E extends RpcEndpoint<infer _Tag, infer R, infer _ConvexFn>
	? Rpc.Success<R>
	: never;

type EndpointError<E> = E extends RpcEndpoint<infer _Tag, infer R, infer _ConvexFn>
	? Rpc.Error<R>
	: never;

type EndpointKind<E> = E extends RpcEndpoint<infer _Tag, infer _R, infer ConvexFn>
	? ConvexFn extends RegisteredQuery<infer _V, infer _A, infer _R2>
		? "query"
		: never
	: never;

export type RpcQueryClient<Payload, Success, Error> = {
	subscription: (
		payload: Payload,
	) => Atom.Atom<Result.Result<Success, Error | RpcDefectError>>;
};

type DecorateEndpoint<E> = EndpointKind<E> extends "query"
	? RpcQueryClient<EndpointPayload<E>, EndpointSuccess<E>, EndpointError<E>>
	: never;

export interface RpcModuleClientConfig {
	readonly layer: Layer.Layer<ConvexClient>;
}

/**
 * The runtime shape {@link createRpcClient} needs: endpoint tag → Convex
 * function reference. Deliberately type-erased — all endpoint typing flows
 * through the `TModule` generic, so callers can supply path-addressed
 * references (e.g. an `anyApi` node) without instantiating the generated
 * api object's type.
 */
export type ConvexApiModule = Record<string, FunctionReference<"query" | "mutation" | "action">>;

type DecorateModuleEndpoints<TModule extends AnyRpcModule> = {
	[K in keyof TModule]: TModule[K] extends RpcEndpoint<string, Rpc.Any, unknown>
		? DecorateEndpoint<TModule[K]>
		: never;
};

type ExtractDecoratedEndpoints<TModule extends AnyRpcModule> =
	Pick<DecorateModuleEndpoints<TModule>, {
		[K in keyof TModule]: TModule[K] extends RpcEndpoint<string, Rpc.Any, unknown> ? K : never;
	}[keyof TModule]>;

export type RpcModuleClient<TModule extends AnyRpcModule> = {
	readonly runtime: Atom.AtomRuntime<ConvexClient>;
} & ExtractDecoratedEndpoints<TModule>;

/**
 * Decode a wire envelope into an Exit. Exported so the wire contract's client
 * side is directly testable; the server side is covered by server.test.ts.
 */
export const decodeExit = (
	encoded: RpcResultEncoded,
): Exit.Exit<unknown, unknown> => {
	// Safety: if the response isn't a valid Confect envelope, wrap as defect
	if (
		!encoded ||
		typeof encoded !== "object" ||
		!("_tag" in encoded)
	) {
		return Exit.fail(
			new RpcDefectError({ defect: `Unexpected RPC response: ${JSON.stringify(encoded)}` }),
		);
	}
	switch (encoded._tag) {
		case "Success":
			return Exit.succeed(encoded.value);
		case "Failure":
			return Exit.fail(encoded.error);
		case "Defect":
			return Exit.fail(new RpcDefectError({ defect: encoded.defect }));
		default:
			return Exit.fail(
				new RpcDefectError({
					defect: `Unexpected envelope tag: ${String((encoded as { _tag: unknown })._tag)}`,
				}),
			);
	}
};

const noop = () => {};

export function createRpcClient<TModule extends AnyRpcModule>(
	convexApi: ConvexApiModule,
	config: RpcModuleClientConfig,
): RpcModuleClient<TModule> {
	const runtime = Atom.runtime(config.layer);

	// One subscription atom family per endpoint tag, built lazily on first
	// property access and cached so repeated access returns the same references.
	const buildEndpointProxy = (tag: string) => {
		const queryRef = convexApi[tag] as FunctionReference<"query">;

		const subscriptionFamily = Atom.family((p: unknown) =>
			runtime.atom(
				Stream.unwrap(
					Effect.gen(function* () {
						const client = yield* ConvexClient;
						return client.subscribe(queryRef, p).pipe(
							Stream.mapEffect((encodedExit) =>
								decodeExit(encodedExit as RpcResultEncoded),
							),
						);
					}),
				),
			),
		);

		return {
			subscription: (payload: unknown) => subscriptionFamily(payload),
		};
	};

	const endpointProxyCache = new Map<string, unknown>();

	const proxy = new Proxy(noop, {
		get(_target, prop) {
			if (prop === "runtime") {
				return runtime;
			}
			if (prop === "then") {
				return undefined;
			}
			if (typeof prop !== "string") {
				return undefined;
			}

			let endpointProxy = endpointProxyCache.get(prop);
			if (!endpointProxy) {
				endpointProxy = buildEndpointProxy(prop);
				endpointProxyCache.set(prop, endpointProxy);
			}
			return endpointProxy;
		},
	});

	return proxy as unknown as RpcModuleClient<TModule>;
}
